import React, { useRef, useMemo, useState, useEffect, useCallback, useLayoutEffect, } from 'react';
import Animated, { runOnJS, useAnimatedRef, useSharedValue, useDerivedValue, useAnimatedScrollHandler, useAnimatedReaction, } from 'react-native-reanimated';
import { useScrollToTop } from '@react-navigation/native';
import { Keyboard, StyleSheet, View, ScrollView } from 'react-native';
import { AppView } from 'design-system/AppView';
import { rem } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import { DEFAULT_SPACE_BETWEEN, } from 'design-system/SelectorControllableReanimated';
import { selectIsEmmaWalkthroughActive, selectIsInvestWalkthroughActive, } from 'reducers/selectors/walkthrough';
import useColors from 'hooks/useColors';
import { useTabNavigation } from 'hooks/navigation/useTabNavigation';
import { useKeyboardAdjustPan } from 'hooks';
import { selectFeatureFlag, selectNavigatorConfig, selectIsGBUser, } from 'reducers/selectors';
import Modal from 'utils/packages/Modal';
import { triggerSelectorPress } from 'utils/hapticFeedback';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { setWalkthroughStatus, setWalkthroughStep } from 'actions';
import { useSearchFieldLearn } from 'features/settings/learn/hooks/useSearchFieldLearn';
import { setInvestWalkthroughStatus } from 'actions/walkthrough';
import { selectTabsPriority } from 'reducers/selectors/organiseTabs';
import { selectWalkthroughAccount } from 'features/invest/reducers/selectors';
import { getSearchResults, getTopTrendingStocks } from 'actions/invest';
import { useIsMainAppWalkthroughV6Active } from 'features/feed/components/walkthrough/hooks/useIsMainAppWalkthroughV6Active';
import Walkthrough from 'features/feed/components/walkthrough/Walkthrough';
import useWalkthroughLayout from 'features/feed/components/walkthrough/useWalkthroughLayout';
import { useShowWalkthroughV6 } from 'features/feed/components/walkthrough/hooks/useShowWalkthroughV6';
import LearnHeader from '../components/LearnHeader';
import LearnCategoryScreen from '../components/LearnCategory';
import { isWeb } from '../../../../constants';
import { emmaWalkthroughData, investWalkthroughData } from '../constants';
const strings = {
    setupInvestModalTitle: 'Walkthrough unavailable',
    setupInvestModalSubtitle: 'To access this walkthrough, you need to have at least one Emma Invest account open.',
    okay: 'Okay',
    openInvest: 'Open Emma Invest account',
};
const SPACING_IN_HEADER = DEFAULT_SPACE_BETWEEN;
const Learn = ({ navigation, route }) => {
    const { navigateToTab } = useTabNavigation();
    const guessedHomeCountry = useAppSelector((store) => store.user.user.guessedHomeCountry);
    const tabPriority = useAppSelector(selectTabsPriority);
    const tabConfig = useAppSelector(selectNavigatorConfig);
    const isEmmaWalkthroughActive = useAppSelector(selectIsEmmaWalkthroughActive);
    const isInvestWalkthroughActive = useAppSelector(selectIsInvestWalkthroughActive);
    const isGBUser = useAppSelector(selectIsGBUser);
    const investWalkthroughAccount = useAppSelector(selectWalkthroughAccount);
    useKeyboardAdjustPan();
    const colors = useColors();
    const sections = useMemo(() => {
        const globalCategories = [
            { name: 'Emma', width: rem(78) },
            { name: 'Track & Budget', width: rem(140) },
        ];
        const GBCategories = [
            { name: 'Save', width: rem(66) },
            ...(isGBUser ? [{ name: 'Invest', width: rem(75) }] : []),
            { name: 'Credit', width: rem(76) },
        ];
        return [
            ...globalCategories,
            ...(guessedHomeCountry === 'GB' ? GBCategories : []),
        ].map((item) => ({
            ...item,
            id: item.name,
            text: item.name,
        }));
    }, [guessedHomeCountry, isGBUser]);
    const { contentWidth: usableWidth, paddingHorizontal } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMemo(() => ({
        width: sections.length * usableWidth,
    }), [sections.length, usableWidth]);
    const horizontalScrollViewRef = useAnimatedRef();
    const categoriesScrollViewRef = useAnimatedRef();
    const categoryScrollViewRefEmma = useAnimatedRef();
    const categoryScrollViewRefTrack = useAnimatedRef();
    const categoryScrollViewRefSave = useAnimatedRef();
    const categoryScrollViewRefInvest = useAnimatedRef();
    const categoryScrollViewRefBorrow = useAnimatedRef();
    const categoryScrollViewRefSearch = useAnimatedRef();
    const scrollX = useSharedValue(0);
    const categoryScrollYEmma = useSharedValue(0);
    const categoryScrollYTrack = useSharedValue(0);
    const categoryScrollYSave = useSharedValue(0);
    const categoryScrollYInvest = useSharedValue(0);
    const categoryScrollYBorrow = useSharedValue(0);
    const categoryScrollYSearch = useSharedValue(0);
    const isUserScrolling = useSharedValue(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const lastSelectedIndex = useRef(selectedIndex);
    const [filteredData, setFilteredData] = useState();
    const learnContent = useAppSelector((store) => selectFeatureFlag(store, 'learn_educational_content_v2')?.extra);
    const { searchTerm, changeSearchTerm, isSearching, cancelSearch } = useSearchFieldLearn('', setFilteredData, learnContent, guessedHomeCountry === 'GB');
    const showWalkthroughV6 = useShowWalkthroughV6();
    const isMainWalkthroughV6Active = useIsMainAppWalkthroughV6Active();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: (isMainWalkthroughV6Active
                ? colors.elements.primaryOnModal
                : colors.text.primary),
        });
    }, [
        colors.text.primary,
        colors.elements.primaryOnModal,
        isMainWalkthroughV6Active,
    ]);
    useDerivedValue(() => {
        let newIndex = 0;
        if (scrollX.value < usableWidth) {
            newIndex = 0;
        }
        else if (scrollX.value < usableWidth * 2) {
            newIndex = 1;
        }
        else if (scrollX.value < usableWidth * 3) {
            newIndex = 2;
        }
        else if (scrollX.value < usableWidth * 4) {
            newIndex = 3;
        }
        else {
            newIndex = 4;
        }
        if (newIndex !== lastSelectedIndex.current) {
            runOnJS(setSelectedIndex)(newIndex);
        }
    }, [usableWidth, scrollX]);
    const scrollY = useDerivedValue(() => {
        if (filteredData) {
            return categoryScrollYSearch.value;
        }
        if (scrollX.value >= usableWidth * 4) {
            return categoryScrollYBorrow.value;
        }
        if (scrollX.value >= usableWidth * 3) {
            return categoryScrollYInvest.value;
        }
        if (scrollX.value >= usableWidth * 2) {
            return categoryScrollYSave.value;
        }
        if (scrollX.value >= usableWidth) {
            return categoryScrollYTrack.value;
        }
        return categoryScrollYEmma.value;
    }, [
        filteredData,
        usableWidth,
        scrollX.value,
        categoryScrollYSearch.value,
        categoryScrollYBorrow.value,
        categoryScrollYInvest.value,
        categoryScrollYSave.value,
        categoryScrollYTrack.value,
        categoryScrollYEmma.value,
    ]);
    const scrollXHandler = useAnimatedScrollHandler({
        onBeginDrag: () => {
            isUserScrolling.value = true;
        },
        onScroll: ({ contentOffset }) => {
            scrollX.value = contentOffset.x;
        },
        onMomentumEnd: () => {
            if (isUserScrolling.value) {
                runOnJS(triggerSelectorPress)();
            }
            isUserScrolling.value = false;
        },
    });
    const onSelectView = useCallback((index, _, animated = true) => {
        if (animated) {
            triggerSelectorPress();
            Keyboard.dismiss();
        }
        horizontalScrollViewRef.current?.scrollTo({
            x: index * usableWidth,
            animated,
        });
    }, [horizontalScrollViewRef, usableWidth]);
    const { itemPositions, scrollHeight, scrollLayoutHeight } = useWalkthroughLayout();
    const paddingHorizontalStyle = useMemo(() => ({
        paddingHorizontal: paddingHorizontal + rem(16),
    }), [paddingHorizontal]);
    const centerOnItem = useCallback((index) => {
        if (categoriesScrollViewRef.current && sections[index]) {
            const item = sections[index];
            const left = sections
                .slice(0, index)
                .reduce((acc, { width }) => acc + width + SPACING_IN_HEADER, 0);
            categoriesScrollViewRef.current.scrollTo({
                x: left +
                    item.width / 2 -
                    (usableWidth / 2 - paddingHorizontalStyle.paddingHorizontal),
                animated: true,
            });
        }
    }, [
        sections,
        usableWidth,
        paddingHorizontalStyle.paddingHorizontal,
        categoriesScrollViewRef,
    ]);
    useAnimatedReaction(() => Math.round(scrollX.value / usableWidth), (currentPage, previousPage) => {
        if (currentPage !== previousPage) {
            runOnJS(centerOnItem)(currentPage);
        }
    });
    useScrollToTop(useRef({
        scrollToTop: () => {
            if (scrollX.value >= usableWidth * 4) {
                categoryScrollViewRefBorrow.current?.scrollTo({ x: 0, y: 0 });
            }
            else if (scrollX.value >= usableWidth * 3) {
                categoryScrollViewRefInvest.current?.scrollTo({ x: 0, y: 0 });
            }
            else if (scrollX.value >= usableWidth * 2) {
                categoryScrollViewRefSave.current?.scrollTo({ x: 0, y: 0 });
            }
            else if (scrollX.value >= usableWidth) {
                categoryScrollViewRefTrack.current?.scrollTo({ x: 0, y: 0 });
            }
            else {
                categoryScrollViewRefEmma.current?.scrollTo({ x: 0, y: 0 });
            }
        },
    }));
    useEffect(() => {
        if (route.params && 'category' in route.params) {
            const { category } = route.params;
            if (category) {
                const x = (() => {
                    switch (category) {
                        case 'emma':
                            return 0;
                        case 'track':
                            return 1;
                        case 'save':
                            return 2;
                        case 'invest':
                            return 3;
                        case 'borrow':
                            return 4;
                        default: {
                            return 0;
                        }
                    }
                })();
                if (x !== undefined) {
                    const timeout = setTimeout(() => {
                        onSelectView(x, undefined, false);
                    }, 10);
                    return () => {
                        clearTimeout(timeout);
                    };
                }
            }
        }
        return undefined;
    }, [route.params, onSelectView]);
    // Start Walkthrough
    const dispatch = useAppDispatch();
    const emmaWalkthroughStart = useCallback(() => {
        if (isEmmaWalkthroughActive || isInvestWalkthroughActive) {
            return;
        }
        dispatch(setWalkthroughStatus('in-progress'));
        dispatch(setWalkthroughStep({
            index: 0,
            highlightTabIndex: 0,
            count: 0,
        }));
        navigation.pop();
        navigation.navigateDeprecated('Feed');
    }, [isEmmaWalkthroughActive, isInvestWalkthroughActive]);
    const investWalkthroughStart = useCallback(() => {
        if (investWalkthroughAccount) {
            dispatch(getTopTrendingStocks(20, 'seccl', investWalkthroughAccount.account.id));
            dispatch(getSearchResults('seccl'));
        }
        navigateToTab('Invest');
        dispatch(setInvestWalkthroughStatus('in-progress'));
    }, [investWalkthroughAccount, navigateToTab]);
    const showSetupInvestModal = useCallback(() => {
        Modal.showConfirmation({
            title: strings.setupInvestModalTitle,
            subtitle: strings.setupInvestModalSubtitle,
            confirmButtonTitle: strings.okay, // secondary
            cancelButtonTitle: strings.openInvest, // primary
            onPressCancel: () => {
                navigateToTab('Invest');
            },
        });
    }, [navigateToTab]);
    const categoryScrollHandlerEmma = useAnimatedScrollHandler((handle) => {
        categoryScrollYEmma.value = handle.contentOffset.y;
    });
    const categoryScrollHandlerTrack = useAnimatedScrollHandler((handle) => {
        categoryScrollYTrack.value = handle.contentOffset.y;
    });
    const categoryScrollHandlerSave = useAnimatedScrollHandler((handle) => {
        categoryScrollYSave.value = handle.contentOffset.y;
    });
    const categoryScrollHandlerInvest = useAnimatedScrollHandler((handle) => {
        categoryScrollYInvest.value = handle.contentOffset.y;
    });
    const categoryScrollHandlerBorrow = useAnimatedScrollHandler((handle) => {
        categoryScrollYBorrow.value = handle.contentOffset.y;
    });
    const categoryScrollHandlerSearch = useAnimatedScrollHandler((handle) => {
        categoryScrollYSearch.value = handle.contentOffset.y;
    });
    const screens = useMemo(() => {
        const globalCategories = [
            {
                scrollRef: categoryScrollViewRefEmma,
                scrollHandler: categoryScrollHandlerEmma,
                content: learnContent?.emma,
                walkthrough: {
                    walkthroughData: emmaWalkthroughData,
                    walkthroughHandler: emmaWalkthroughStart,
                    show: learnContent?.emma?.showWalkthrough && !isEmmaWalkthroughActive,
                },
                initialTitle: 'emma',
            },
            {
                scrollRef: categoryScrollViewRefTrack,
                scrollHandler: categoryScrollHandlerTrack,
                content: learnContent?.track,
                initialTitle: 'track',
            },
        ];
        const investWalkthroughCardConditions = learnContent?.invest?.showWalkthrough &&
            !isInvestWalkthroughActive &&
            !isEmmaWalkthroughActive &&
            tabConfig.showInvestTab &&
            tabPriority.priority.includes('invest');
        const canStartInvestWalkthrough = investWalkthroughAccount != null && investWalkthroughCardConditions;
        const GBCategories = [
            {
                scrollRef: categoryScrollViewRefSave,
                scrollHandler: categoryScrollHandlerSave,
                content: learnContent?.save,
                initialTitle: 'save',
            },
            ...(isGBUser
                ? [
                    {
                        scrollRef: categoryScrollViewRefInvest,
                        scrollHandler: categoryScrollHandlerInvest,
                        content: learnContent?.invest,
                        initialTitle: 'invest',
                        walkthrough: {
                            walkthroughData: investWalkthroughData,
                            show: investWalkthroughCardConditions,
                            walkthroughHandler: canStartInvestWalkthrough
                                ? investWalkthroughStart
                                : showSetupInvestModal,
                        },
                    },
                ]
                : []),
            {
                scrollRef: categoryScrollViewRefBorrow,
                scrollHandler: categoryScrollHandlerBorrow,
                content: learnContent?.borrow,
                initialTitle: 'borrow',
            },
        ];
        return [
            ...globalCategories,
            ...(guessedHomeCountry === 'GB' ? GBCategories : []),
        ];
    }, [
        categoryScrollViewRefEmma,
        categoryScrollHandlerEmma,
        learnContent?.emma,
        learnContent?.track,
        learnContent?.save,
        learnContent?.invest,
        learnContent?.borrow,
        emmaWalkthroughStart,
        isEmmaWalkthroughActive,
        isInvestWalkthroughActive,
        categoryScrollViewRefTrack,
        categoryScrollHandlerTrack,
        categoryScrollViewRefSave,
        categoryScrollHandlerSave,
        isGBUser,
        categoryScrollViewRefInvest,
        categoryScrollHandlerInvest,
        investWalkthroughAccount,
        showSetupInvestModal,
        tabConfig.showInvestTab,
        tabPriority.priority,
        investWalkthroughStart,
        categoryScrollViewRefBorrow,
        categoryScrollHandlerBorrow,
        guessedHomeCountry,
    ]);
    const LearnCategoryScreenWrapper = isWeb ? ScrollView : View;
    return (<AppView type="secondary" withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <LearnHeader scrollY={scrollY} scrollX={scrollX} sections={sections} onCancel={cancelSearch} searchTerm={searchTerm} isSearching={isSearching} screenWidth={usableWidth} setSelection={onSelectView} onChangeSearchTerm={changeSearchTerm} containerStyle={paddingHorizontalStyle} categoriesScrollViewRef={categoriesScrollViewRef}/>
      <View style={styles.flex}>
        {!searchTerm && !isSearching ? (<Animated.ScrollView horizontal pagingEnabled decelerationRate="fast" scrollEventThrottle={16} onScroll={scrollXHandler} ref={horizontalScrollViewRef} keyboardDismissMode="on-drag" keyboardShouldPersistTaps="handled" showsHorizontalScrollIndicator={false} contentContainerStyle={contentContainerStyle}>
            {screens.map((screen, index) => (<LearnCategoryScreenWrapper style={{ width: usableWidth, flex: 1 }} key={index}>
                <LearnCategoryScreen learnContent={screen.content} scrollViewRef={screen.scrollRef} initialTitle={screen.initialTitle} showWalkthrough={screen.walkthrough?.show} categoryScrollHandler={screen.scrollHandler} walkThroughData={screen.walkthrough?.walkthroughData} walkThroughHandler={screen.walkthrough?.walkthroughHandler}/>
              </LearnCategoryScreenWrapper>))}
          </Animated.ScrollView>) : (<View style={styles.flex}>
            <LearnCategoryScreen learnContent={filteredData} scrollViewRef={categoryScrollViewRefSearch} categoryScrollHandler={categoryScrollHandlerSearch}/>
          </View>)}
        {!!showWalkthroughV6 && (<Walkthrough scrollHeight={scrollHeight} itemPositions={itemPositions} scrollLayoutHeight={scrollLayoutHeight}/>)}
      </View>
    </AppView>);
};
const styles = StyleSheet.create({
    flex: {
        flex: 1,
    },
});
export default Learn;
